import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import useGallery from '@/src/GalleryConfiguration/components/hooks/useGallery';
import { useEventContext } from '../components/EventContext';
import { dedupeShares } from '../lib/eventStats';
import useShare from '@/src/Share/components/hooks/useShare';
import useSurveyQuestions from '@/src/GalleryConfiguration/components/hooks/useSurveyQuestions';
import useTemplates, {
    EmailTemplate,
} from '@/src/EmailTemplates/components/hooks/useTemplates';
import CodeEditor from '@uiw/react-textarea-code-editor';
import { Switch } from '@/src/shadComponents/ui/switch';
import usePsv2Dark from '../components/usePsv2Dark';
import WebhookSection from '../components/WebhookSection';
import ReengageDialog from '../components/ReengageDialog';

/*
 * Sharing v2 — the guest's share journey as an ordered stepper
 * (order decided 2026-07: age gate → disclaimer → pre-share survey →
 * post-share survey → share methods), with an outcomes rail (shares
 * sent/unsent, recent shares, reach). Config lives on the gallery
 * object and saves whole via PUT /galleries/:id, same as Gallery v2.
 */

interface StepShellProps {
    n: number;
    title: string;
    desc: string;
    on: boolean;
    onToggle: (v: boolean) => void;
    last?: boolean;
    children?: React.ReactNode;
}

function Step({ n, title, desc, on, onToggle, last, children }: StepShellProps) {
    return (
        <div className="relative pb-[26px] pl-[34px]">
            {!last && (
                <span className="absolute bottom-0 left-[10px] top-6 w-px bg-line" />
            )}
            <span
                className={
                    'absolute left-0 top-px flex h-[21px] w-[21px] items-center justify-center rounded-full border-[1.5px] font-mono text-[10px] ' +
                    (on
                        ? 'border-brand bg-brand text-brand-foreground'
                        : 'border-border bg-background text-faint')
                }
            >
                {n}
            </span>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div
                        className={
                            'text-[14px] font-semibold tracking-[-0.01em] ' +
                            (on ? '' : 'text-faint')
                        }
                    >
                        {title}
                    </div>
                    <div
                        className={
                            'mt-0.5 max-w-[46ch] text-[12px] ' +
                            (on ? 'text-muted-foreground' : 'text-faint')
                        }
                    >
                        {desc}
                    </div>
                </div>
                <Switch checked={on} onCheckedChange={onToggle} />
            </div>
            {on && children && <div className="mt-3">{children}</div>}
        </div>
    );
}

function Chk({
    on,
    label,
    onClick,
}: {
    on: boolean;
    label: string;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={
                'cursor-pointer rounded-full px-3 py-[5px] font-[inherit] text-[12px] font-semibold ' +
                (on
                    ? 'border border-transparent bg-brand-soft text-brand-ink'
                    : 'border border-border bg-transparent text-muted-foreground')
            }
        >
            {label}
        </button>
    );
}

function Field({
    label,
    optional,
    mono,
    value,
    placeholder,
    onChange,
}: {
    label: string;
    optional?: string;
    mono?: boolean;
    value: string;
    placeholder?: string;
    onChange: (v: string) => void;
}) {
    return (
        <div className="mt-[13px] first:mt-0">
            <label className="mb-1.5 block text-[12px] font-semibold text-muted-foreground">
                {label}
                {optional && (
                    <span className="font-normal text-faint"> {optional}</span>
                )}
            </label>
            <input
                className={
                    'ps-input w-full' +
                    (mono ? ' font-mono text-[12.5px]' : '')
                }
                value={value}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
            />
        </div>
    );
}

export default function SharingV2() {
    const navigate = useNavigate();
    const { activationId } = useParams();
    const id = activationId ? +activationId : 0;
    const { getGallery, saveGallery } = useGallery();
    const {
        galleryId: ctxGalleryId,
        resolved,
        ensureSessions,
        refreshGallery,
    } = useEventContext();
    const { getSharesForActivation, reprocessUnsentShares } = useShare();
    const { getSurveyQuestions } = useSurveyQuestions();
    const { getTemplates } = useTemplates();
    const isDark = usePsv2Dark();

    const [gallery, setGallery] = useState<any>(null);
    const [form, setForm] = useState<any>(null);
    const [snapshot, setSnapshot] = useState('');
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [shares, setShares] = useState<any[]>([]);
    const [sessionCount, setSessionCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [savedAt, setSavedAt] = useState<number | null>(null);
    /* "Saved just now" decays to plain "Saved" */
    useEffect(() => {
        if (!savedAt) return;
        const timer = setTimeout(() => setSavedAt(null), 10 * 1000);
        return () => clearTimeout(timer);
    }, [savedAt]);
    const [saveError, setSaveError] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    const [showReengage, setShowReengage] = useState(false);

    const fromGallery = (g: any, questions: any[]) => {
        const activeQuestions = (questions ?? []).filter(
            (q: any) => !q.isDeleted,
        );
        return {
            ageGate: !!g.ageVerification,
            verifiedAge: g.verifiedAge != null ? String(g.verifiedAge) : '21',
            disclaimerOn: !!g.disclaimerEnabled,
            disclaimerRequired: !!g.disclaimerRequired,
            disclaimerOptOut: !!g.disclaimerOptOut,
            disclaimer: g.disclaimer ?? '',
            preShareOn:
                !!g.preShareSurveyUrl ||
                activeQuestions.length > 0 ||
                !!g.surveyRequired,
            preShareSurveyUrl: g.preShareSurveyUrl ?? '',
            surveyRequired: !!g.surveyRequired,
            questions: (questions ?? []).map((q: any) => ({
                id: q.id,
                question: q.question ?? '',
                answerTypeId: q.answerTypeId ?? 2,
                isDeleted: !!q.isDeleted,
            })),
            postShareOn: !!g.surveyUrl,
            surveyUrl: g.surveyUrl ?? '',
            emailEnabled: !!g.emailEnabled,
            textEnabled: !!g.textEnabled,
            fromName: g.fromName ?? '',
            fromAddress: g.fromAddress ?? '',
            subject: g.subject ?? '',
            emailBody: g.emailBody ?? '',
            textBody: g.textBody ?? '',
            sendLinkToPhoto: !!g.sendLinkToPhoto,
            showGalleryLink: !!g.showGalleryLink,
            /* absent (older server) means shown, matching the DB default */
            saveForLaterEnabled: g.saveForLaterEnabled !== false,
            sendAttachments: !!g.sendAttachments,
            shareLinkTarget: g.shareLinkTarget ?? 'activationGallery',
            createShareDescriptionText: g.createShareDescriptionText ?? '',
            createShareDescriptionTextSize:
                g.createShareDescriptionTextSize != null
                    ? String(g.createShareDescriptionTextSize)
                    : '',
            createShareDescriptionTextFont:
                g.createShareDescriptionTextFont ?? '',
        };
    };

    const loadShares = async () => {
        const raw = await getSharesForActivation(id).catch(() => []);
        return dedupeShares(raw);
    };

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const [sessions, uniqShares] = await Promise.all([
                    ensureSessions(),
                    loadShares(),
                ]);
                if (!mounted) return;
                setShares(uniqShares);
                setSessionCount(sessions.length);
                if (ctxGalleryId != null) {
                    const [full, questions, templateRows] =
                        await Promise.all([
                            getGallery(ctxGalleryId),
                            getSurveyQuestions(id).catch(() => []),
                            getTemplates().catch(() => []),
                        ]);
                    if (!mounted) return;
                    setGallery(full);
                    setTemplates(templateRows ?? []);
                    const f = fromGallery(full, questions ?? []);
                    setForm(f);
                    setSnapshot(JSON.stringify(f));
                }
            } catch (e) {
                /* empty state handles it */
            } finally {
                if (mounted) setIsLoading(false);
            }
        };
        if (resolved) load();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, resolved]);

    const dirty = useMemo(() => {
        if (!gallery || !form) return false;
        return JSON.stringify(form) !== snapshot;
    }, [gallery, form, snapshot]);

    const set = (key: string, value: any) => {
        setForm((f: any) => ({ ...f, [key]: value }));
        setSaveError(false);
    };

    const save = async () => {
        if (!gallery || !form || isSaving) return;
        setIsSaving(true);
        setSaveError(false);
        /* toggling the survey step off retires the whole step: url,
           required flag, and every built-in question (soft delete) */
        const questions = form.preShareOn
            ? form.questions.filter((q: any) => q.id != null || !q.isDeleted)
            : form.questions.map((q: any) => ({ ...q, isDeleted: true }));
        const payload = {
            ...gallery,
            ageVerification: form.ageGate,
            verifiedAge: form.ageGate
                ? Number(form.verifiedAge) || null
                : null,
            disclaimerEnabled: form.disclaimerOn,
            disclaimerRequired: form.disclaimerOn
                ? form.disclaimerRequired
                : false,
            disclaimerOptOut: form.disclaimerOn
                ? form.disclaimerOptOut
                : false,
            disclaimer: form.disclaimer,
            preShareSurveyUrl: form.preShareOn
                ? form.preShareSurveyUrl || null
                : null,
            surveyRequired: form.preShareOn ? form.surveyRequired : false,
            surveyQuestions: questions.filter(
                (q: any) => q.id != null || q.question.trim(),
            ),
            surveyUrl: form.postShareOn ? form.surveyUrl || null : null,
            emailEnabled: form.emailEnabled,
            textEnabled: form.textEnabled,
            fromName: form.fromName,
            fromAddress: form.fromAddress,
            subject: form.subject,
            emailBody: form.emailBody,
            textBody: form.textBody,
            sendLinkToPhoto: form.sendLinkToPhoto,
            showGalleryLink: form.showGalleryLink,
            saveForLaterEnabled: form.saveForLaterEnabled,
            sendAttachments: form.sendAttachments,
            shareLinkTarget: form.shareLinkTarget,
            createShareDescriptionText: form.createShareDescriptionText,
            createShareDescriptionTextSize:
                Number(form.createShareDescriptionTextSize) || null,
            createShareDescriptionTextFont:
                form.createShareDescriptionTextFont,
        };
        const ok = await saveGallery(gallery.id, payload).catch(() => false);
        setIsSaving(false);
        if (ok) {
            setGallery(payload);
            /* re-fetch questions — the server assigns ids to new rows */
            const freshQuestions = await getSurveyQuestions(id).catch(
                () => [],
            );
            const f = fromGallery(payload, freshQuestions ?? []);
            setForm(f);
            setSnapshot(JSON.stringify(f));
            setSavedAt(Date.now());
            refreshGallery();
        } else {
            setSaveError(true);
        }
    };

    const stats = useMemo(() => {
        const sent = shares.filter((s: any) => s.sent).length;
        const unsent = shares.length - sent;
        const destinations = new Set(
            shares
                .map((s: any) => s.destination)
                .filter(Boolean),
        );
        const sessionsShared = new Set(
            shares.map((s: any) => s.photoSessionId).filter(Boolean),
        );
        const rate =
            sessionCount > 0
                ? Math.round((sessionsShared.size / sessionCount) * 100)
                : null;
        const recent = [...shares]
            .sort(
                (a: any, b: any) =>
                    moment(b.createdAt ?? b.created_at).valueOf() -
                    moment(a.createdAt ?? a.created_at).valueOf(),
            )
            .slice(0, 3);
        return { sent, unsent, reached: destinations.size, rate, recent };
    }, [shares, sessionCount]);

    const methodsOn = form?.emailEnabled || form?.textEnabled;

    const retryUnsent = async () => {
        if (isRetrying) return;
        setIsRetrying(true);
        try {
            await reprocessUnsentShares(id);
            setShares(await loadShares());
        } catch {
            /* rail keeps prior counts */
        } finally {
            setIsRetrying(false);
        }
    };

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
                {[0, 1, 2].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-16 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </main>
        );
    }

    if (!gallery || !form) {
        return (
            <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
                <div className="ps-label mb-4">Sharing</div>
                <p className="text-[13.5px] text-muted-foreground">
                    Sharing is configured on the gallery — this activation has
                    no gallery yet.{' '}
                    <button
                        type="button"
                        onClick={() => navigate(`/activations/${id}/galleries`)}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] font-semibold text-brand-ink"
                    >
                        Create one in the classic app →
                    </button>
                </p>
            </main>
        );
    }

    return (
        <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-2">Sharing</div>
            <div className="mb-6 mt-0.5 text-[13px] text-muted-foreground">
                The guest's share journey, in order — configure each step
                below.{' '}
                <b className="font-semibold text-foreground">
                    {stats.sent.toLocaleString()} share
                    {stats.sent === 1 ? '' : 's'}
                </b>{' '}
                sent so far.
            </div>

            <div className="grid gap-0 md:grid-cols-[minmax(0,1fr)_300px]">
                {/* JOURNEY */}
                <div className="md:pr-[42px]">
                    <div className="mb-3.5 flex h-7 items-center justify-between">
                        <span className="ps-label">Share journey</span>
                        <span className="flex items-center gap-3">
                            {saveError && (
                                <span className="text-[11.5px] font-semibold text-attention">
                                    Save failed — try again
                                </span>
                            )}
                            {dirty ? (
                                <button
                                    type="button"
                                    onClick={save}
                                    disabled={isSaving}
                                    className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground"
                                >
                                    {isSaving ? 'Saving…' : 'Save changes'}
                                </button>
                            ) : (
                                <span className="text-[11.5px] text-faint">
                                    {savedAt ? 'Saved just now' : 'Saved'}
                                </span>
                            )}
                        </span>
                    </div>

                    <Step
                        n={1}
                        title="Age gate"
                        desc="Ask guests to confirm their age before anything else."
                        on={form.ageGate}
                        onToggle={(v) => set('ageGate', v)}
                    >
                        <div className="mt-[13px] w-32">
                            <label className="mb-1.5 block text-[12px] font-semibold text-muted-foreground">
                                Minimum age
                            </label>
                            <input
                                className="ps-input w-full tabular-nums"
                                inputMode="numeric"
                                value={form.verifiedAge}
                                onChange={(e) =>
                                    set(
                                        'verifiedAge',
                                        e.target.value.replace(/\D/g, ''),
                                    )
                                }
                            />
                        </div>
                    </Step>

                    <Step
                        n={2}
                        title="Disclaimer"
                        desc="Guests see and accept your terms before sharing."
                        on={form.disclaimerOn}
                        onToggle={(v) => set('disclaimerOn', v)}
                    >
                        <div className="mt-1 flex flex-wrap gap-1.5">
                            <Chk
                                on={form.disclaimerRequired}
                                label="Require acceptance"
                                onClick={() =>
                                    set(
                                        'disclaimerRequired',
                                        !form.disclaimerRequired,
                                    )
                                }
                            />
                            <Chk
                                on={form.disclaimerOptOut}
                                label="Opt-out style"
                                onClick={() =>
                                    set(
                                        'disclaimerOptOut',
                                        !form.disclaimerOptOut,
                                    )
                                }
                            />
                        </div>
                        <Field
                            label="Disclaimer text"
                            value={form.disclaimer}
                            placeholder="By sharing you agree that…"
                            onChange={(v) => set('disclaimer', v)}
                        />
                    </Step>

                    <Step
                        n={3}
                        title="Pre-share survey"
                        desc="Built-in questions, an external survey, or both — before the share is sent."
                        on={form.preShareOn}
                        onToggle={(v) => set('preShareOn', v)}
                    >
                        <div className="mb-1 mt-1">
                            <div className="mb-1.5 flex items-center gap-2.5">
                                <span className="text-[12px] font-semibold text-muted-foreground">
                                    Built-in questions
                                </span>
                                <Chk
                                    on={form.surveyRequired}
                                    label="Require answers"
                                    onClick={() =>
                                        set(
                                            'surveyRequired',
                                            !form.surveyRequired,
                                        )
                                    }
                                />
                            </div>
                            {form.questions
                                .map((q: any, qi: number) => ({ q, qi }))
                                .filter(({ q }: any) => !q.isDeleted)
                                .map(({ q, qi }: any) => (
                                    <div
                                        key={q.id ?? `new-${qi}`}
                                        className="mb-2 flex items-center gap-2.5"
                                    >
                                        <input
                                            className="ps-input flex-1 text-[13px]"
                                            placeholder="Question"
                                            value={q.question}
                                            onChange={(e) =>
                                                setForm((f: any) => ({
                                                    ...f,
                                                    questions: f.questions.map(
                                                        (x: any, i: number) =>
                                                            i === qi
                                                                ? {
                                                                      ...x,
                                                                      question:
                                                                          e
                                                                              .target
                                                                              .value,
                                                                  }
                                                                : x,
                                                    ),
                                                }))
                                            }
                                        />
                                        <select
                                            className="ps-input w-[92px] cursor-pointer appearance-none text-[12.5px]"
                                            value={q.answerTypeId}
                                            onChange={(e) =>
                                                setForm((f: any) => ({
                                                    ...f,
                                                    questions: f.questions.map(
                                                        (x: any, i: number) =>
                                                            i === qi
                                                                ? {
                                                                      ...x,
                                                                      answerTypeId:
                                                                          +e
                                                                              .target
                                                                              .value,
                                                                  }
                                                                : x,
                                                    ),
                                                }))
                                            }
                                        >
                                            <option value={2}>Text</option>
                                            <option value={1}>Yes/No</option>
                                        </select>
                                        <button
                                            type="button"
                                            title="Remove question"
                                            onClick={() =>
                                                setForm((f: any) => ({
                                                    ...f,
                                                    questions:
                                                        q.id != null
                                                            ? f.questions.map(
                                                                  (
                                                                      x: any,
                                                                      i: number,
                                                                  ) =>
                                                                      i === qi
                                                                          ? {
                                                                                ...x,
                                                                                isDeleted:
                                                                                    true,
                                                                            }
                                                                          : x,
                                                              )
                                                            : f.questions.filter(
                                                                  (
                                                                      _: any,
                                                                      i: number,
                                                                  ) =>
                                                                      i !==
                                                                      qi,
                                                              ),
                                                }))
                                            }
                                            className="cursor-pointer border-0 bg-transparent p-0 text-[13px] text-faint hover:text-attention"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            <button
                                type="button"
                                onClick={() =>
                                    setForm((f: any) => ({
                                        ...f,
                                        questions: [
                                            ...f.questions,
                                            {
                                                question: '',
                                                answerTypeId: 2,
                                            },
                                        ],
                                    }))
                                }
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                            >
                                + Add question
                            </button>
                        </div>
                        <Field
                            label="External survey URL"
                            optional="— optional"
                            mono
                            value={form.preShareSurveyUrl}
                            placeholder="https://"
                            onChange={(v) => set('preShareSurveyUrl', v)}
                        />
                    </Step>

                    <Step
                        n={4}
                        title="Post-share survey"
                        desc="Runs after the share is sent."
                        on={form.postShareOn}
                        onToggle={(v) => set('postShareOn', v)}
                    >
                        <Field
                            label="Survey URL"
                            mono
                            value={form.surveyUrl}
                            placeholder="https://"
                            onChange={(v) => set('surveyUrl', v)}
                        />
                    </Step>

                    <Step
                        n={5}
                        title="Share methods"
                        desc="How guests send photos to themselves. Off until you enable a method."
                        on={!!methodsOn}
                        onToggle={(v) => {
                            if (v) {
                                set('emailEnabled', true);
                            } else {
                                setForm((f: any) => ({
                                    ...f,
                                    emailEnabled: false,
                                    textEnabled: false,
                                }));
                            }
                        }}
                        last
                    >
                        <div className="mb-2.5 flex gap-1.5">
                            <Chk
                                on={form.emailEnabled}
                                label={
                                    form.emailEnabled ? '✓ Email' : 'Email'
                                }
                                onClick={() =>
                                    set('emailEnabled', !form.emailEnabled)
                                }
                            />
                            <Chk
                                on={form.textEnabled}
                                label={
                                    form.textEnabled
                                        ? '✓ Text message'
                                        : 'Text message'
                                }
                                onClick={() =>
                                    set('textEnabled', !form.textEnabled)
                                }
                            />
                        </div>
                        {form.emailEnabled && (
                            <>
                                <div className="grid gap-x-[22px] md:grid-cols-2">
                                    <Field
                                        label="Email from name"
                                        value={form.fromName}
                                        onChange={(v) => set('fromName', v)}
                                    />
                                    <Field
                                        label="From address"
                                        mono
                                        value={form.fromAddress}
                                        onChange={(v) =>
                                            set('fromAddress', v)
                                        }
                                    />
                                </div>
                                <Field
                                    label="Email subject"
                                    value={form.subject}
                                    onChange={(v) => set('subject', v)}
                                />
                                <div className="mt-[13px]">
                                    <div className="mb-1.5 flex items-baseline justify-between">
                                        <label className="text-[12px] font-semibold text-muted-foreground">
                                            Email body
                                        </label>
                                        {templates.length > 0 && (
                                            <select
                                                className="cursor-pointer border-0 bg-transparent font-[inherit] text-[11.5px] font-semibold text-brand-ink outline-none"
                                                value=""
                                                onChange={(e) => {
                                                    const t =
                                                        templates.find(
                                                            (x) =>
                                                                String(
                                                                    x.id,
                                                                ) ===
                                                                e.target
                                                                    .value,
                                                        );
                                                    if (t)
                                                        set(
                                                            'emailBody',
                                                            t.body,
                                                        );
                                                }}
                                            >
                                                <option value="">
                                                    Apply a template…
                                                </option>
                                                {templates.map((t) => (
                                                    <option
                                                        key={t.id}
                                                        value={String(t.id)}
                                                    >
                                                        {t.name}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                    <div className="overflow-hidden rounded-[10px] border border-border">
                                        <CodeEditor
                                            value={form.emailBody}
                                            language="html"
                                            onChange={(e) =>
                                                set(
                                                    'emailBody',
                                                    e.target.value,
                                                )
                                            }
                                            padding={12}
                                            data-color-mode={isDark ? 'dark' : 'light'}
                                            style={{
                                                fontFamily:
                                                    'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                fontSize: 11.5,
                                                background: 'transparent',
                                                minHeight: 120,
                                                maxHeight: 280,
                                                overflow: 'auto',
                                                overflowWrap: 'anywhere',
                                                wordBreak: 'break-word',
                                            }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        {form.textEnabled && (
                            <Field
                                label="Text message body"
                                value={form.textBody}
                                onChange={(v) => set('textBody', v)}
                            />
                        )}
                        <div className="mt-3.5 flex flex-wrap gap-1.5">
                            <Chk
                                on={form.sendLinkToPhoto}
                                label="Link to photo"
                                onClick={() =>
                                    set(
                                        'sendLinkToPhoto',
                                        !form.sendLinkToPhoto,
                                    )
                                }
                            />
                            <Chk
                                on={form.showGalleryLink}
                                label="Show gallery link"
                                onClick={() =>
                                    set(
                                        'showGalleryLink',
                                        !form.showGalleryLink,
                                    )
                                }
                            />
                            <Chk
                                on={form.saveForLaterEnabled}
                                label="Save For Later button"
                                onClick={() =>
                                    set(
                                        'saveForLaterEnabled',
                                        !form.saveForLaterEnabled,
                                    )
                                }
                            />
                            <Chk
                                on={form.sendAttachments}
                                label="Send as attachment"
                                onClick={() =>
                                    set(
                                        'sendAttachments',
                                        !form.sendAttachments,
                                    )
                                }
                            />
                        </div>
                        <div className="mt-4 grid gap-x-[22px] md:grid-cols-2">
                            <div>
                                <label className="mb-1.5 block text-[12px] font-semibold text-muted-foreground">
                                    Share links open
                                </label>
                                <select
                                    className="ps-input w-full cursor-pointer appearance-none text-[13px]"
                                    value={form.shareLinkTarget}
                                    onChange={(e) =>
                                        set(
                                            'shareLinkTarget',
                                            e.target.value,
                                        )
                                    }
                                >
                                    <option value="activationGallery">
                                        Event gallery
                                    </option>
                                    <option value="sessionGallery">
                                        Session gallery
                                    </option>
                                    <option value="personalGallery">
                                        Personal gallery
                                    </option>
                                    <option value="jobGallery">
                                        Job gallery
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div className="mt-4">
                            <div className="mb-1.5 text-[12px] font-semibold text-muted-foreground">
                                Sharing-station copy
                            </div>
                            <div className="grid gap-x-[22px] md:grid-cols-[minmax(0,1fr)_90px_140px]">
                                <Field
                                    label="Text"
                                    value={form.createShareDescriptionText}
                                    placeholder="Shown above the share form"
                                    onChange={(v) =>
                                        set('createShareDescriptionText', v)
                                    }
                                />
                                <Field
                                    label="Size"
                                    value={
                                        form.createShareDescriptionTextSize
                                    }
                                    placeholder="16"
                                    onChange={(v) =>
                                        set(
                                            'createShareDescriptionTextSize',
                                            v.replace(/\D/g, ''),
                                        )
                                    }
                                />
                                <Field
                                    label="Font"
                                    value={
                                        form.createShareDescriptionTextFont
                                    }
                                    placeholder="Inherit"
                                    onChange={(v) =>
                                        set(
                                            'createShareDescriptionTextFont',
                                            v,
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </Step>
                </div>

                {/* RAIL — outcomes */}
                <div className="mt-9 md:mt-0 md:border-l md:border-line md:pl-[42px]">
                    <div className="mb-[30px]">
                        <div className="flex items-baseline justify-between">
                            <span className="ps-label">Shares</span>
                            <button
                                type="button"
                                onClick={() =>
                                    navigate(
                                        `/v2/activations/${id}/sharing/shares`,
                                    )
                                }
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                            >
                                All →
                            </button>
                        </div>
                        <div className="mb-1 mt-2.5 flex gap-[22px]">
                            <div>
                                <div className="text-[20px] font-semibold tracking-[-0.01em] tabular-nums">
                                    {stats.sent.toLocaleString()}
                                </div>
                                <div className="mt-px text-[11px] text-muted-foreground">
                                    Sent
                                </div>
                            </div>
                            <div>
                                <div className="text-[20px] font-semibold tracking-[-0.01em] tabular-nums">
                                    {stats.unsent.toLocaleString()}
                                </div>
                                <div
                                    className={
                                        'mt-px text-[11px] ' +
                                        (stats.unsent > 0
                                            ? 'font-semibold text-attention'
                                            : 'text-muted-foreground')
                                    }
                                >
                                    Unsent
                                </div>
                            </div>
                        </div>
                        {stats.recent.map((s: any, i: number) => (
                            <div
                                key={s.id ?? i}
                                className={
                                    'flex items-center gap-2.5 py-2 text-[12.5px] ' +
                                    (i > 0 ? 'border-t border-line' : '')
                                }
                            >
                                <div className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-muted-foreground">
                                    {s.destination || '—'}
                                </div>
                                <div className="ml-auto font-mono text-[10px] uppercase text-faint">
                                    {s.type === 'text' ? 'SMS' : s.type}
                                </div>
                            </div>
                        ))}
                        {stats.unsent > 0 && (
                            <button
                                type="button"
                                onClick={retryUnsent}
                                disabled={isRetrying}
                                className="mt-2.5 w-full cursor-pointer rounded-[9px] border border-border bg-transparent px-3 py-2 text-left font-[inherit] text-[12.5px] font-semibold text-foreground hover:border-faint"
                            >
                                {isRetrying
                                    ? 'Retrying…'
                                    : `Retry ${stats.unsent} unsent share${
                                          stats.unsent === 1 ? '' : 's'
                                      }`}
                            </button>
                        )}
                        {stats.reached > 0 && (
                            <button
                                type="button"
                                onClick={() => setShowReengage(true)}
                                className="mt-2.5 w-full cursor-pointer rounded-[9px] border border-border bg-transparent px-3 py-2 text-left font-[inherit] text-[12.5px] font-semibold text-foreground hover:border-faint"
                            >
                                Reengage {stats.reached.toLocaleString()} guest
                                {stats.reached === 1 ? '' : 's'} →
                            </button>
                        )}
                    </div>

                    <div>
                        <div className="ps-label mb-2">
                            Guests in this event
                        </div>
                        <div className="mb-1 mt-2.5 flex gap-[22px]">
                            <div>
                                <div className="text-[20px] font-semibold tracking-[-0.01em] tabular-nums">
                                    {stats.reached.toLocaleString()}
                                </div>
                                <div className="mt-px text-[11px] text-muted-foreground">
                                    People reached
                                </div>
                            </div>
                            {stats.rate != null && (
                                <div>
                                    <div className="text-[20px] font-semibold tracking-[-0.01em] tabular-nums">
                                        {stats.rate}%
                                    </div>
                                    <div className="mt-px text-[11px] text-muted-foreground">
                                        Share rate
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* per-event share webhook (classic's "Integrations" tab) */}
            <WebhookSection activationId={id} />

            {showReengage && (
                <ReengageDialog
                    activationId={id}
                    onClose={() => setShowReengage(false)}
                />
            )}
        </main>
    );
}
